import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { AdminSessionAllowancePanel } from "@/components/AdminSessionAllowancePanel";
import { AdminUserLanguageControl } from "@/components/AdminUserLanguageControl";
import {
  AdminAccountSharingAlerts,
  AdminLoginRiskPanel,
} from "@/components/AdminAccountSharingReview";

/*
 * Admin Panel — Section 2 (User Accounts) + Section 4 (deletion).
 *
 * Left column: search results plus account-sharing review alerts.
 * Right column: detail card for the selected user with actions:
 *   - Login-pattern / account-sharing review history
 *   - Daily interview-session allowance controls (audit-logged)
 *   - Adjust credits (+/-) with reason (legacy, audit-logged)
 *   - Grant / revoke Beta access
 *   - Suspend / re-activate account
 *   - Revoke active sessions (force sign-out)
 *   - Delete account (Section 4)
 * Credit ledger is shown inline while the legacy credit model is retired.
 */

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

const STATUS_PILL = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  suspended: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function AdminUsers() {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const search = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", {
        params: q ? { q, limit: 100 } : { limit: 100 },
      });
      setRows(data.users);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (userId) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetail(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { search(""); }, [search]);
  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const onSubmit = (e) => { e.preventDefault(); search(query.trim()); };

  const adjustCredits = async () => {
    if (!detail) return;
    const raw = window.prompt(
      "Amount (positive to add, negative to debit). Balance cannot go below 0.",
      "",
    );
    if (raw === null || raw.trim() === "") return;
    const delta = parseInt(raw, 10);
    if (Number.isNaN(delta) || delta === 0) {
      toast.error("Enter a non-zero integer");
      return;
    }
    const reason = window.prompt("Reason (required, shown in audit + ledger):", "");
    if (!reason || !reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await api.post(`/admin/users/${detail.user.user_id}/credits`, {
        delta,
        reason: reason.trim(),
      });
      toast.success(`Credits updated (${delta > 0 ? "+" : ""}${delta})`);
      loadDetail(detail.user.user_id);
      search(query.trim());
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const toggleBeta = async () => {
    if (!detail) return;
    const target = !detail.user.beta_access_approved;
    const reason = window.prompt(
      target ? "Reason to grant Beta access:" : "Reason to revoke Beta access:",
      "",
    );
    if (reason === null) return;
    try {
      await api.patch(`/admin/users/${detail.user.user_id}/beta-access`, {
        beta_access_approved: target,
        reason: reason.trim() || null,
      });
      toast.success(target ? "Beta granted" : "Beta revoked");
      loadDetail(detail.user.user_id);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const toggleStatus = async () => {
    if (!detail) return;
    const next = detail.user.account_status === "active" ? "suspended" : "active";
    const reason = window.prompt(
      next === "suspended"
        ? "Reason to suspend this account? (Active sessions will be revoked.)"
        : "Reason to re-activate this account?",
      "",
    );
    if (reason === null) return;
    try {
      await api.patch(`/admin/users/${detail.user.user_id}/status`, {
        account_status: next,
        reason: reason.trim() || null,
      });
      toast.success(next === "suspended" ? "Account suspended" : "Account re-activated");
      loadDetail(detail.user.user_id);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const revokeSessions = async () => {
    if (!detail) return;
    if (!window.confirm("Force sign-out of every active session for this user?")) return;
    try {
      const { data } = await api.post(
        `/admin/users/${detail.user.user_id}/revoke-sessions`,
        { reason: "admin_revoke" },
      );
      toast.success(`Revoked ${data.revoked} session(s)`);
      loadDetail(detail.user.user_id);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  const deleteUser = async () => {
    if (!detail) return;
    if (detail.user.is_admin) {
      toast.error("Admin accounts cannot be deleted from the panel");
      return;
    }
    const email = detail.user.email;
    const typed = window.prompt(
      `This will permanently delete ${email}, anonymise their ledger + consent records, and drop every session.\n\nType the user's email to confirm:`,
      "",
    );
    if (typed !== email) {
      if (typed !== null) toast.error("Email did not match, aborted");
      return;
    }
    const reason = window.prompt("Reason for deletion (audit-logged):", "");
    const retention = window.prompt(
      "Retention exception? (optional — e.g. 'legal hold on audit records for 90d')",
      "",
    );
    try {
      const { data } = await api.delete(`/admin/users/${detail.user.user_id}`, {
        data: {
          confirm: true,
          reason: (reason || "").trim() || null,
          retention_exception: (retention || "").trim() || null,
        },
      });
      toast.success(`Deleted. Anonymised reference: ${data.anon_ref}`);
      setSelectedId(null);
      search(query.trim());
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    }
  };

  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-users">
      <AdminNav title="User Accounts" />

      <main className="max-w-7xl mx-auto p-6 grid gap-6 lg:grid-cols-[2fr_3fr]">
        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-4 flex flex-col min-h-[70vh]">
          <AdminAccountSharingAlerts onSelectUser={setSelectedId} />

          <form onSubmit={onSubmit} className="flex gap-2 mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email, first name, or user_id"
              className="flex-1 rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-sm"
              data-testid="admin-users-search-input"
            />
            <button
              type="submit"
              className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-4 py-2 text-sm font-medium"
              data-testid="admin-users-search-btn"
            >
              Search
            </button>
          </form>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-sm text-[color:var(--text-secondary)]">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-4 text-sm text-[color:var(--text-secondary)]">No users match.</div>
            ) : (
              <ul className="space-y-1" data-testid="admin-users-list">
                {rows.map((u) => (
                  <li key={u.user_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(u.user_id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-xs transition ${
                        selectedId === u.user_id
                          ? "border-[color:var(--brand)] bg-[color:var(--bg-surface-hover)]"
                          : "border-transparent hover:bg-[color:var(--bg-surface-hover)]"
                      }`}
                      data-testid={`admin-users-row-${u.user_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono truncate">{u.email}</span>
                        <span className={`ml-2 shrink-0 inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_PILL[u.account_status] || "bg-slate-100 border-slate-200"}`}>
                          {u.account_status}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-[color:var(--text-secondary)] flex gap-3 flex-wrap">
                        <span>{u.first_name || "—"}</span>
                        <span>{u.country || "—"}</span>
                        <span>{u.auth_provider}</span>
                        {u.beta_access_approved && <span className="text-emerald-700">Beta ✓</span>}
                        {u.is_admin && <span className="text-[color:var(--brand)]">Admin</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 min-h-[70vh]" data-testid="admin-user-detail">
          {!selectedId ? (
            <div className="text-sm text-[color:var(--text-secondary)]">Select a user on the left to see full details.</div>
          ) : detailLoading || !detail ? (
            <div className="text-sm text-[color:var(--text-secondary)]">Loading user…</div>
          ) : (
            <UserDetailBody
              detail={detail}
              onAdjustCredits={adjustCredits}
              onToggleBeta={toggleBeta}
              onToggleStatus={toggleStatus}
              onRevokeSessions={revokeSessions}
              onDeleteUser={deleteUser}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function UserDetailBody({ detail, onAdjustCredits, onToggleBeta, onToggleStatus, onRevokeSessions, onDeleteUser }) {
  const u = detail.user;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">{u.auth_provider}</div>
          <div className="text-lg font-semibold" data-testid="admin-user-detail-email">{u.email}</div>
          <div className="text-xs text-[color:var(--text-secondary)] font-mono">{u.user_id}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_PILL[u.account_status] || "bg-slate-100"}`}>{u.account_status}</span>
          {u.beta_access_approved && <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-0.5 text-[10px] uppercase tracking-widest">Beta approved</span>}
          {u.is_admin && <span className="inline-block rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] px-2 py-0.5 text-[10px] uppercase tracking-widest">Admin</span>}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <Field label="First name" value={u.first_name || "—"} />
        <Field label="Country" value={u.country || "—"} />
        <Field label="Active sessions" value={detail.active_sessions} />
        <Field label="Created" value={fmt(u.created_at)} />
        <Field label="Last login" value={fmt(u.last_login_at)} />
        <Field label="Consent" value={u.privacy_consent?.accepted ? `${u.privacy_consent.policy_version} · ${u.privacy_consent.policy_language}` : "—"} />
        <Field label="Redeemed beta code" value={detail.redeemed_beta_code?.code || "—"} />
      </div>

      <AdminLoginRiskPanel userId={u.user_id} />

      <AdminUserLanguageControl userId={u.user_id} />

      <AdminSessionAllowancePanel userId={u.user_id} isAdminUser={u.is_admin} />

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[color:var(--border-stone)]">
        <ActionBtn onClick={onAdjustCredits} testId="admin-adjust-credits">Adjust credits (legacy)</ActionBtn>
        <ActionBtn onClick={onToggleBeta} testId="admin-toggle-beta">{u.beta_access_approved ? "Revoke Beta" : "Grant Beta"}</ActionBtn>
        <ActionBtn onClick={onToggleStatus} testId="admin-toggle-status">{u.account_status === "active" ? "Suspend" : "Re-activate"}</ActionBtn>
        <ActionBtn onClick={onRevokeSessions} testId="admin-revoke-sessions">Revoke sessions ({detail.active_sessions})</ActionBtn>
        <ActionBtn onClick={onDeleteUser} testId="admin-delete-user" disabled={u.is_admin} intent="danger">Delete account</ActionBtn>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)] mb-1">Legacy credit ledger ({detail.credit_ledger.length})</div>
        {detail.credit_ledger.length === 0 ? (
          <div className="text-xs text-[color:var(--text-secondary)]">No ledger entries yet.</div>
        ) : (
          <div className="overflow-x-auto border border-[color:var(--border-stone)] rounded-lg">
            <table className="w-full text-xs" data-testid="admin-user-ledger">
              <thead className="bg-[color:var(--bg-surface-hover)] text-[color:var(--text-secondary)] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-right px-3 py-2">Δ</th>
                  <th className="text-right px-3 py-2">Balance</th>
                  <th className="text-left px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {detail.credit_ledger.map((l) => (
                  <tr key={l.entry_id} className="border-t border-[color:var(--border-stone)]">
                    <td className="px-3 py-1.5 whitespace-nowrap">{fmt(l.at)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{l.kind}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${l.delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{l.balance_after}</td>
                    <td className="px-3 py-1.5 break-all">{l.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</div>
      <div className="text-sm text-[color:var(--text-primary)]" data-testid={testId}>{value}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, testId, disabled, intent }) {
  const base = "rounded-full px-4 py-1.5 text-xs font-medium border transition disabled:opacity-40";
  const style = intent === "danger"
    ? "border-rose-300 text-rose-700 hover:bg-rose-50"
    : "border-[color:var(--border-stone)] text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]";
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} className={`${base} ${style}`}>
      {children}
    </button>
  );
}
